import type { ExportRow } from './csv';
import { EXPORT_FORMAT_MIME_TYPES } from '../../constants/export';

const DEFAULT_SHEET_NAME = 'Export';

// Excel refuses these in a sheet name, and silently truncates past 31 characters.
const INVALID_SHEET_NAME_CHARS = /[*?:\\/[\]]/g;
const MAX_SHEET_NAME_LENGTH = 31;

const MIN_COLUMN_WIDTH = 10;
const MAX_COLUMN_WIDTH = 60;

export function sheetName(name: string | undefined): string {
  const cleaned = (name ?? '').replace(INVALID_SHEET_NAME_CHARS, ' ').trim();
  return cleaned === '' ? DEFAULT_SHEET_NAME : cleaned.slice(0, MAX_SHEET_NAME_LENGTH);
}

// Widths are in characters, not pixels, and exceljs applies no autofit — without this every column
// arrives at the default width and long audit-log descriptions read as truncated until the reader
// drags each border.
export function columnWidth(header: string, values: readonly string[]): number {
  const longest = values.reduce((max, value) => Math.max(max, value.length), header.length);
  return Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, longest + 2));
}

/**
 * Writes an .xlsx workbook from the same `(headers, rows, columnIds)` shape `generateCSV` takes, so
 * a caller offering both formats maps its rows once.
 *
 * Numbers are written as number cells rather than strings so Excel can sum and sort them; nothing
 * else is coerced. There is no `escapeFormulae` equivalent to apply, and none needed: exceljs writes
 * a string as a string-typed cell, and only an explicit `{ formula }` value becomes a formula — so
 * unlike the CSV path, a description beginning with `=` cannot execute on open.
 *
 * exceljs is imported dynamically because it is far larger than the rest of this package combined;
 * the chunk is fetched when a reader first picks Excel and never on a page that only renders a table.
 */
export async function generateXLSX(
  headers: string[],
  rows: ExportRow[],
  columnIds: string[],
  options?: { readonly sheetName?: string },
): Promise<Blob> {
  const { default: ExcelJS } = await import('exceljs');

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(sheetName(options?.sheetName));

  const cells = rows.map((row) =>
    columnIds.map((id) => {
      const value = row[id];
      if (typeof value === 'number') {
        return value;
      }
      return value === null || value === undefined ? '' : String(value);
    }),
  );

  worksheet.columns = columnIds.map((id, index) => ({
    header: headers[index] ?? id,
    key: id,
    width: columnWidth(
      headers[index] ?? id,
      cells.map((cell) => String(cell[index] ?? '')),
    ),
  }));
  worksheet.addRows(cells);

  worksheet.getRow(1).font = { bold: true };
  // Keeps the header in view while scrolling a few hundred rows of history.
  worksheet.views = [{ state: 'frozen', ySplit: 1 }];

  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], { type: EXPORT_FORMAT_MIME_TYPES.xlsx });
}
